import { Fragment } from 'react';
import PropTypes from 'prop-types'
import compose from 'lodash/flowRight';
import { graphql } from '@apollo/client/react/hoc';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Loading from '../loading';
import ErrorMessage from '../error-message';
import NotFound from '../not-found';
import InviteList from '../invite-list';
import {
  eventInvitesQuery,
  expireEventInviteMutation,
  extendEventInviteMutation,
  resendEventEmailInviteMutation
} from '../../apollo/events';
import NewLinkInvite from './new-link-invite';
import NewEmailInvites from './new-email-invites';
import CopyInput from '../copy-input';
import config from '../../config';

const useStyles = makeStyles((theme) => ({
  publicLink: {
    margin: theme.spacing(2, 0),
    padding: theme.spacing(2),
    border: `2px solid ${theme.palette.secondary.main}`,
    '& > p:first-child': {
      marginTop: 0
    }
  }
}));

const EventInvitesTab = (props) => {
  const {
    loading,
    error,
    event,
    expireEventInvite,
    extendEventInvite,
    resendEventEmailInvite
  } = props;
  const classes = useStyles();

  if (error) {
    return <ErrorMessage message='An error occurred while loading invites.' error={error} compact />;
  }
  if (loading) { return <Loading compact />; }
  if (!event || !event.permissions.editable) { return <NotFound compact />; }

  const inviteListProps = {
    basePath: `/events/${event.id}`,
    expireInvite: expireEventInvite,
    extendInvite: extendEventInvite,
    resendEmailInvite: resendEventEmailInvite
  };

  return (
    <Fragment>
      {event.public && (
        <Paper className={classes.publicLink}>
          <p>Special invites are not required for public events. Anyone with a link to the event can join.</p>
          <CopyInput text={`${config().universal.baseUri}/events/${event.id}`} />
        </Paper>
      )}

      <Typography variant="h2">Link Invites</Typography>
      <InviteList invites={event.linkInvites} {...inviteListProps} />
      <NewLinkInvite event={event} />

      <Typography variant="h2">Email Invites</Typography>
      <InviteList invites={event.emailInvites} {...inviteListProps} />
      <NewEmailInvites event={event} />
    </Fragment>
  );
};

EventInvitesTab.propTypes = {
  event: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.object,
  expireEventInvite: PropTypes.func,
  extendEventInvite: PropTypes.func,
  resendEventEmailInvite: PropTypes.func
};

export default compose(
  graphql(eventInvitesQuery, {
    props: ({ data: { event, loading, error } }) => {
      return {
        event,
        loading,
        error
      };
    },
    options: ({ event }) => ({
      variables: {
        id: event.id
      }
    })
  }),
  graphql(expireEventInviteMutation, {
    name: 'expireEventInvite'
  }),
  graphql(extendEventInviteMutation, {
    name: 'extendEventInvite'
  }),
  graphql(resendEventEmailInviteMutation, {
    name: 'resendEventEmailInvite'
  })
)(EventInvitesTab);
